import { LEGAL_DOCUMENTS, type LegalDocumentKey } from '@hue/contracts';
import { StatusPill } from '@hue/design-system';
import { PortalFooter, PortalMasthead } from './portal-masthead.js';

/// O canal do encarregado de dados da plataforma (LGPD, art. 41). Enquanto a plataforma não o
/// definir, a política diz isso em vez de inventar um endereço (ADR 0067, decisão 10).
export const PRIVACY_OFFICER_CONTACT =
  'a ser definido pela plataforma antes de o portal ser publicado';

interface Section {
  title: string;
  paragraphs?: string[];
  items?: string[];
  table?: Array<[string, string, string]>;
}

/// Rascunho redigido com o usuário em 15/10/2026. Precisa de revisão jurídica antes de qualquer
/// empresa publicar o portal (ADR 0066, decisão 11; ADR 0067, decisão 10).
const documents: Record<LegalDocumentKey, Section[]> = {
  PRIVACY: [
    {
      title: 'Quem cuida dos seus dados',
      paragraphs: [
        'A plataforma HUE• é controladora dos dados da sua conta: nome, como você quer ser chamado(a), e-mail, celular, senha, preferências e os aceites destes documentos.',
        'Quando você pede um horário numa empresa, ela passa a ter um cadastro de cliente seu e é controladora desse cadastro. A plataforma trata esse cadastro em nome dela, como operadora.',
      ],
    },
    {
      title: 'O que coletamos e para quê',
      table: [
        ['Criar e manter sua conta', 'Nome, e-mail, senha', 'Execução de contrato (art. 7º, V)'],
        [
          'Enviar seus pedidos e as mensagens sobre eles',
          'Nome, e-mail, celular, pedidos',
          'Execução de contrato (art. 7º, V)',
        ],
        [
          'Novidades e promoções de uma empresa',
          'Nome, e-mail, celular',
          'Consentimento (art. 7º, I), por empresa e revogável',
        ],
        [
          'Pagamento de adiantamento',
          'CPF, valor, situação do pagamento',
          'Execução de contrato e obrigação legal (art. 7º, V e II)',
        ],
        [
          'Segurança e prevenção de fraude',
          'Registros de acesso e do navegador',
          'Legítimo interesse (art. 7º, IX) e Marco Civil da Internet',
        ],
      ],
    },
    {
      title: 'Com quem compartilhamos',
      items: [
        'Com a empresa em que você pede um horário, só a partir desse pedido. Uma empresa não vê o que você tem em outra.',
        'Com o provedor de pagamento, quando você paga um adiantamento. Os dados do cartão ficam com ele, nunca conosco.',
        'Com o serviço que entrega nossos e-mails.',
      ],
    },
    {
      title: 'Por quanto tempo',
      paragraphs: [
        'Sua conta fica enquanto você a mantiver. Ao excluí-la, apagamos o que é da plataforma, exceto os registros de acesso que a lei manda guardar (Marco Civil da Internet). O cadastro mantido por cada empresa segue as regras dela, como a guarda fiscal das vendas.',
      ],
    },
    {
      title: 'Seus direitos (art. 18)',
      items: [
        'Confirmar que tratamos seus dados, acessá-los, corrigi-los e receber uma cópia.',
        'Ligar ou desligar as novidades de cada empresa quando quiser.',
        'Excluir sua conta.',
        'Saber com quem compartilhamos seus dados.',
      ],
      paragraphs: [
        'Quase tudo se faz em Minha conta › Meus dados e mensagens e em Minha conta › Privacidade. Pedidos sobre o cadastro mantido por uma empresa vão para ela.',
      ],
    },
    {
      title: 'Menores de idade',
      paragraphs: [
        'A conta é só para quem tem 18 anos ou mais. Menores são atendidos por meio do responsável.',
      ],
    },
    {
      title: 'Encarregado de dados',
      paragraphs: [`Canal do encarregado da plataforma: ${PRIVACY_OFFICER_CONTACT}.`],
    },
    {
      title: 'Mudanças nesta política',
      paragraphs: [
        'Quando a política mudar, a data da versão muda, e pedimos seu aceite de novo antes do próximo pedido de horário.',
      ],
    },
  ],
  TERMS: [
    {
      title: 'O que é o portal',
      paragraphs: [
        'Uma vitrine em que você encontra empresas e pede horários. O serviço é prestado pela empresa escolhida; a plataforma leva o seu pedido até ela.',
      ],
    },
    {
      title: 'Sua conta',
      items: [
        'Uma conta por pessoa, para quem tem 18 anos ou mais.',
        'O e-mail precisa ser seu e estar correto: as confirmações de agendamento chegam por ele.',
        'Você cuida da sua senha e, se ligar, do seu código de segurança.',
      ],
    },
    {
      title: 'Pedidos de horário',
      paragraphs: [
        'Pedir ou pagar não confirma o horário: quem confirma é a empresa. Cada pedido traz a política de cancelamento que você aceita antes de enviar.',
        'No primeiro pedido numa empresa, ela recebe seu nome, e-mail e celular para atender o pedido.',
      ],
    },
    {
      title: 'Mudanças nestes termos',
      paragraphs: [
        'Quando os termos mudarem, a data da versão muda, e pedimos seu aceite de novo antes do próximo pedido de horário.',
      ],
    },
  ],
};

export function LegalDocumentScreen({ document }: { document: LegalDocumentKey }) {
  const { label, version } = LEGAL_DOCUMENTS[document];
  return (
    <div className="public-portal min-h-screen bg-surface text-content">
      <PortalMasthead />
      <main className="mx-auto max-w-3xl px-5 pb-20 pt-10 sm:px-8">
        <StatusPill tone="warning">Rascunho · exige revisão jurídica</StatusPill>
        <h1 className="mt-4 text-3xl font-semibold tracking-tight sm:text-4xl">{label}</h1>
        <p className="mt-2 text-sm text-content-muted">
          Versão de {version.split('-').reverse().join('/')}
        </p>
        {documents[document].map((section) => (
          <section key={section.title} className="mt-8">
            <h2 className="text-lg font-semibold">{section.title}</h2>
            {section.table ? (
              <div className="mt-3 overflow-x-auto rounded-xl border border-surface-border">
                <table className="w-full text-left text-sm">
                  <thead className="bg-surface-elevated text-xs text-content-muted">
                    <tr>
                      <th className="px-4 py-2 font-semibold">Uso</th>
                      <th className="px-4 py-2 font-semibold">Dados</th>
                      <th className="px-4 py-2 font-semibold">Base legal (LGPD)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {section.table.map(([use, data, basis]) => (
                      <tr key={use} className="border-t border-surface-border">
                        <td className="px-4 py-3">{use}</td>
                        <td className="px-4 py-3 text-content-muted">{data}</td>
                        <td className="px-4 py-3 text-content-muted">{basis}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
            {section.items ? (
              <ul className="mt-3 list-disc space-y-2 pl-5 text-sm leading-7 text-content-secondary">
                {section.items.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            ) : null}
            {section.paragraphs?.map((paragraph) => (
              <p key={paragraph} className="mt-3 text-sm leading-7 text-content-secondary">
                {paragraph}
              </p>
            ))}
          </section>
        ))}
      </main>
      <PortalFooter />
    </div>
  );
}
